import { createSelector } from "reselect";
import { sortCompare } from "~/utils/utils.jsx";
import { getActiveProjects, getProjectMembersById } from "./selector.projects.jsx";

const getFirstArg = (state, arg) => arg;

export const getAllCompanies = (state) => state.companies;

export const getCompanyById = createSelector(getAllCompanies, getFirstArg, (companies, id) => companies[id]);

export const getCompaniesArray = createSelector(getAllCompanies, (companies) => Object.values(companies));

export const getAllCompaniesSortedByName = createSelector(getCompaniesArray, (companies) =>
  companies.sort((company1, company2) => company1.name.localeCompare(company2.name))
);

export const getCompaniesByIds = createSelector(
  getAllCompanies,
  (_state, ids) => ids,
  (companies, ids) =>
    ids
      .map((id) => companies[id])
      .filter(Boolean)
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()))
);

export const getAllActiveCompaniesSortedByName = createSelector(getAllCompaniesSortedByName, (companies) =>
  companies.filter((company) => company.is_archived === false && company.is_trashed === false)
);

export const getAllActiveCompaniesSortedByNameWithOwnerCompanyFirst = createSelector(
  getAllActiveCompaniesSortedByName,
  (companies) => {
    const ownerCompany = companies.find((company) => company.is_owner);
    const otherCompanies = companies.filter((company) => company.id !== ownerCompany?.id);

    return ownerCompany ? [ownerCompany, ...otherCompanies] : otherCompanies;
  }
);

export const getAllActiveCompaniesSortedByNameWithoutOwnerCompany = createSelector(
  getAllActiveCompaniesSortedByName,
  (companies) => {
    return companies
      .filter((company) => !company.is_owner)
      .map((c) => {
        return { id: c.id, name: c.name };
      });
  }
);

export const makeCompaniesByProjectMembersSelector = (projectId) =>
  createSelector(
    (state) => getProjectMembersById(state, projectId),
    getCompaniesArray,
    (projectMembers, companies) =>
      companies.filter((company) => projectMembers.some((userId) => company.members.some((id) => id === userId)))
  );

export const makeGetCompaniesProjectCount = () => {
  return createSelector(
    (state) => getCompaniesArray(state),
    (state) => getActiveProjects(state),
    (companies, activeProjects) => {
      const result = {};

      activeProjects.toArray().forEach((project) => {
        if (project.company_id) {
          if (!result[project.company_id]) result[project.company_id] = 0;
          result[project.company_id]++;
        }
      });

      return result;
    }
  );
};

export const getOwnersCompany = createSelector(getAllCompanies, (companies) =>
  Object.values(companies).find((company) => company.is_owner)
);

export const getCompanyNamesWithIds = createSelector(getAllActiveCompaniesSortedByName, (companies) => {
  return companies.reduce((acc, company) => {
    const name = company.name;
    acc[name] = company.id;

    return acc;
  }, {});
});
